/**
 * Simple IndexedDB utility for storing large data
 */

import {
  DB_NAME,
  DB_VERSION,
  READ_ONLY,
  READ_WRITE,
  STORE_NAME,
} from "../constants"

export const isSupported = () => {
  return typeof window !== "undefined" && "indexedDB" in window
}
/**
 * Opens IndexedDB connection
 * @return {Promise<IDBDatabase>}
 */
const openDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)

    request.onupgradeneeded = event => {
      const db = event.target.result
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: "key" })
      }
    }
  })
}

/**
 * Save data to IndexedDB
 * @param {string} key - Storage key
 * @param {any} data - Data to store
 * @return {Promise<void>}
 */
export const setItem = async (key, data) => {
  try {
    const db = await openDB()
    const transaction = db.transaction(STORE_NAME, READ_WRITE)
    const store = transaction.objectStore(STORE_NAME)

    return new Promise((resolve, reject) => {
      const request = store.put({ key, data })
      request.onsuccess = () => {
        db.close()
        resolve()
      }
      request.onerror = () => {
        db.close()
        reject(request.error)
      }
    })
  } catch (error) {
    console.error("IndexedDB setItem error:", error)
    throw error
  }
}

/**
 * Get data from IndexedDB
 * @param {string} key - Storage key
 * @return {Promise<any>}
 */
export const getItem = async key => {
  try {
    const db = await openDB()
    const transaction = db.transaction(STORE_NAME, READ_ONLY)
    const store = transaction.objectStore(STORE_NAME)

    return new Promise((resolve, reject) => {
      const request = store.get(key)
      request.onsuccess = () => {
        db.close()
        resolve(request.result?.data || null)
      }
      request.onerror = () => {
        db.close()
        reject(request.error)
      }
    })
  } catch (error) {
    console.error("IndexedDB getItem error:", error)
    return null
  }
}

/**
 * Remove data from IndexedDB
 * @param {string} key - Storage key
 * @return {Promise<void>}
 */
export const removeItem = async key => {
  try {
    const db = await openDB()
    const transaction = db.transaction(STORE_NAME, READ_WRITE)
    const store = transaction.objectStore(STORE_NAME)

    return new Promise((resolve, reject) => {
      const request = store.delete(key)
      request.onsuccess = () => {
        db.close()
        resolve()
      }
      request.onerror = () => {
        db.close()
        reject(request.error)
      }
    })
  } catch (error) {
    console.error("IndexedDB removeItem error:", error)
    throw error
  }
}

/**
 * Clear all data from IndexedDB
 * @return {Promise<void>}
 */
export const clear = async () => {
  try {
    const db = await openDB()
    const transaction = db.transaction(STORE_NAME, READ_WRITE)
    const store = transaction.objectStore(STORE_NAME)

    return new Promise((resolve, reject) => {
      const request = store.clear()
      request.onsuccess = () => {
        db.close()
        resolve()
      }
      request.onerror = () => {
        db.close()
        reject(request.error)
      }
    })
  } catch (error) {
    console.error("IndexedDB clear error:", error)
    throw error
  }
}
